import { useRef, useState } from "react";
import { Upload, X, AlertCircle, Image as ImageIcon, Cloud, Loader2 } from "lucide-react";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { isSupabaseConnected, uploadImage } from "../supabase";

interface ImageUploadProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
  /** Accepted MIME types, e.g. ["image/svg+xml","image/png","image/jpeg"] */
  accept?: string[];
  /** Human-readable list shown under the upload button */
  acceptLabel?: string;
  /** Maximum file size in bytes */
  maxSizeBytes?: number;
  /** Human-readable max size, e.g. "500 KB" */
  maxSizeLabel?: string;
  /** Preview aspect ratio class, e.g. "aspect-square" or "aspect-video" */
  previewAspect?: string;
  /** Extra classes for the preview container */
  previewClassName?: string;
}

const DEFAULT_ACCEPT = ["image/png", "image/jpeg", "image/webp"];
const DEFAULT_ACCEPT_LABEL = "PNG, JPG, WEBP";
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024; // 5 MB
const DEFAULT_MAX_SIZE_LABEL = "5 MB";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ImageUpload({
  label,
  value,
  onChange,
  accept = DEFAULT_ACCEPT,
  acceptLabel = DEFAULT_ACCEPT_LABEL,
  maxSizeBytes = DEFAULT_MAX_SIZE,
  maxSizeLabel = DEFAULT_MAX_SIZE_LABEL,
  previewAspect = "aspect-square",
  previewClassName = "",
}: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState<"upload" | "url">("upload");
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const sbConnected = isSupabaseConnected();

  const isSvg = (src: string) =>
    src.startsWith("data:image/svg") || src.endsWith(".svg");

  const handleFile = async (file: File) => {
    setError(null);

    // Check MIME type
    if (!accept.includes(file.type)) {
      setError(`Invalid format. Accepted: ${acceptLabel}`);
      return;
    }

    // Check size
    if (file.size > maxSizeBytes) {
      setError(`File too large (${formatBytes(file.size)}). Maximum: ${maxSizeLabel}`);
      return;
    }

    // If Supabase is connected, upload to Storage bucket
    if (sbConnected) {
      setUploading(true);
      const { url, error: uploadErr } = await uploadImage(file, "admin");
      setUploading(false);
      if (uploadErr) {
        setError(`Upload failed: ${uploadErr}`);
        // Fall back to base64
        readAsDataUrl(file);
        return;
      }
      if (url) {
        onChange(url);
        return;
      }
    }

    // Fallback: read as data URL (base64)
    readAsDataUrl(file);
  };

  const readAsDataUrl = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) onChange(result);
    };
    reader.onerror = () => setError("Failed to read file.");
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so same file can be re-selected
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleUrlApply = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    // Only allow HTTPS URLs and data:image URIs (no plain HTTP)
    if (!/^https:\/\//i.test(trimmed) && !/^data:image\//i.test(trimmed)) {
      setError("Only HTTPS or data:image URLs are allowed.");
      return;
    }
    onChange(trimmed);
    setUrlInput("");
    setError(null);
  };

  const handleClear = () => {
    onChange("");
    setError(null);
  };

  return (
    <div>
      <label
        className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2"
        style={{ fontSize: "10px" }}
      >
        {label}
      </label>

      {/* Preview */}
      {value && (
        <div className="relative mb-3 group">
          <div
            className={`${previewAspect} ${previewClassName} rounded-lg overflow-hidden bg-[#F2EEE8] border border-[#E6E0D7]`}
          >
            {isSvg(value) ? (
              <img
                src={value}
                alt={`${label} preview`}
                className="w-full h-full object-contain p-4"
              />
            ) : (
              <ImageWithFallback
                src={value}
                alt={`${label} preview`}
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 w-7 h-7 bg-[#2D2A26]/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#2D2A26]"
            title="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Mode switcher */}
      <div className="flex gap-1 mb-3">
        <button
          type="button"
          onClick={() => setMode("upload")}
          className={`px-3 py-1.5 rounded-lg tracking-[0.08em] transition-all ${
            mode === "upload"
              ? "bg-[#6F7F63]/10 text-[#6F7F63] border border-[#6F7F63]/30"
              : "text-[#6F675F] border border-[#E6E0D7] hover:bg-[#F2EEE8]"
          }`}
          style={{ fontSize: "11px" }}
        >
          Upload file
        </button>
        <button
          type="button"
          onClick={() => setMode("url")}
          className={`px-3 py-1.5 rounded-lg tracking-[0.08em] transition-all ${
            mode === "url"
              ? "bg-[#6F7F63]/10 text-[#6F7F63] border border-[#6F7F63]/30"
              : "text-[#6F675F] border border-[#E6E0D7] hover:bg-[#F2EEE8]"
          }`}
          style={{ fontSize: "11px" }}
        >
          Paste URL
        </button>
      </div>

      {mode === "upload" ? (
        <>
          {/* Drop zone */}
          <div
            role="button"
            tabIndex={0}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
            }}
            className={`border-2 border-dashed rounded-lg py-6 px-4 flex flex-col items-center gap-2 cursor-pointer transition-all ${
              dragOver
                ? "border-[#6F7F63] bg-[#6F7F63]/5"
                : "border-[#E6E0D7] bg-[#F7F5F1] hover:border-[#C7A76C] hover:bg-[#C7A76C]/5"
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-[#E6E0D7]/50 flex items-center justify-center">
              {value ? (
                <ImageIcon size={18} className="text-[#6F7F63]" />
              ) : (
                <Upload size={18} className="text-[#6F675F]" />
              )}
            </div>
            <p className="text-[#6F675F] text-center" style={{ fontSize: "12px" }}>
              {value ? "Replace image" : "Click or drag to upload"}
            </p>
            <p className="text-[#D8D1C7]" style={{ fontSize: "10px" }}>
              {acceptLabel} &middot; Max {maxSizeLabel}
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={accept.join(",")}
            onChange={handleInputChange}
            className="hidden"
          />
        </>
      ) : (
        /* URL input */
        <div className="flex gap-2">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleUrlApply();
            }}
            placeholder="https://example.com/image.png"
            className="flex-1 px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C]"
            style={{ fontSize: "13px" }}
          />
          <button
            type="button"
            onClick={handleUrlApply}
            disabled={!urlInput.trim()}
            className="px-4 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase disabled:opacity-40"
            style={{ fontSize: "11px" }}
          >
            Apply
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 mt-2 text-[#8B5C58]">
          <AlertCircle size={14} />
          <p style={{ fontSize: "12px" }}>{error}</p>
        </div>
      )}
    </div>
  );
}